import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, AlertTriangle, HelpCircle, ChevronRight, Mail, Printer } from "lucide-react";
import {
  briefing,
  moved,
  crisis,
  recommendations,
  qmsr,
  radar,
  suppliers,
  assets,
  trace,
} from "@/data/fixtures";
import BrandMark from "@/components/BrandMark";
import ApprovalModal from "@/components/ApprovalModal";

const SectionLabel = ({ children, tone }) => {
  const map = {
    green: { color: "var(--mos-green)", ring: "var(--mos-green-border)" },
    amber: { color: "var(--mos-amber)", ring: "var(--mos-amber-border)" },
    slate: { color: "var(--mos-text-muted)", ring: "var(--mos-border-strong)" },
  };
  const s = map[tone] || map.slate;
  return (
    <div className="flex items-center gap-3 mb-4">
      <span
        className="inline-block h-1.5 w-1.5 rounded-full"
        style={{ background: s.color }}
      />
      <span
        className="text-[11px] tracking-[0.18em] font-semibold uppercase"
        style={{ color: s.color }}
      >
        {children}
      </span>
      <span
        className="flex-1 h-px"
        style={{ background: s.ring, opacity: 0.6 }}
      />
    </div>
  );
};

export default function DailyBriefing() {
  const navigate = useNavigate();
  const rec = recommendations[0];
  const alt = recommendations[1];
  const [sendOpen, setSendOpen] = useState(false);

  const onSendApproved = () => {
    setSendOpen(false);
    toast.success(
      "Briefing sent · j.okafor@meridian.example · May 23 09:14 · PDF archived",
      { duration: 4600 }
    );
  };

  const onPreviewPDF = () => window.print();

  return (
    <div data-testid="page-daily-briefing" className="space-y-10">
      {/* Print-only artifact header · appears only when printing */}
      <div className="print-only" style={{ marginBottom: "18pt" }}>
        <div
          className="flex items-center justify-between pb-4 border-b"
          style={{ borderColor: "#0F172A" }}
        >
          <div className="flex items-center gap-2">
            <BrandMark size={20} variant="default" />
            <span
              className="text-[13pt] font-semibold tracking-tight"
              style={{ color: "#1E3A5F" }}
            >
              Manufacturing<span className="mos-mono" style={{ color: "#64748B" }}>OS</span>
            </span>
            <span className="text-[10pt] text-slate-500">· Meridian Surgical Systems</span>
          </div>
          <div className="text-[9pt] text-slate-500 mos-mono">
            trace ID · rt-2026-05-23-0658
          </div>
        </div>
      </div>

      {/* Memo header · with takeaway actions on the right */}
      <div className="flex items-end justify-between gap-8">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-slate-500 mb-3">
            Daily Briefing · Executive Memo
          </div>
          <h1 className="text-[42px] leading-[1.05] font-semibold text-slate-900 tracking-tight">
            {briefing.date}
          </h1>
          <div className="mt-3 text-[15px] text-slate-600">
            Prepared for{" "}
            <span className="text-slate-900 font-medium">{briefing.addressee}</span>.
          </div>
        </div>

        <div className="flex items-center gap-2 no-print pb-1">
          <button
            type="button"
            onClick={onPreviewPDF}
            className="mos-btn mos-btn-secondary"
            data-testid="btn-preview-pdf"
            title="Opens the browser print dialog · save as PDF from there"
          >
            <Printer size={14} />
            Preview PDF
          </button>
          <button
            type="button"
            onClick={() => setSendOpen(true)}
            className="mos-btn mos-btn-primary"
            data-testid="btn-send-briefing"
          >
            <Mail size={14} />
            Send briefing
          </button>
        </div>
      </div>

      {/* WHAT MOVED */}
      <section data-testid="section-what-moved">
        <SectionLabel tone="green">What moved</SectionLabel>
        <div className="grid grid-cols-2 gap-4">
          {moved.map((m) => (
            <div
              key={m.id}
              className="mos-surface rounded-md p-5 flex items-start gap-4"
              data-testid={`moved-item-${m.id}`}
            >
              <span
                className="mt-0.5 h-7 w-7 flex-none inline-flex items-center justify-center rounded-full"
                style={{ background: "var(--mos-green-bg)" }}
              >
                <Check size={14} style={{ color: "var(--mos-green)" }} />
              </span>
              <div>
                <div className="text-[15px] font-medium text-slate-900">
                  {m.title}
                </div>
                <div className="text-[13.5px] text-slate-600 mt-1">
                  {m.detail}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT IS BLOCKED */}
      <section data-testid="section-what-blocked">
        <SectionLabel tone="amber">What is blocked</SectionLabel>
        <button
          type="button"
          onClick={() => navigate("/reasoning")}
          className="w-full text-left mos-surface rounded-md p-6 border-l-4 hover:shadow-sm transition-shadow group"
          style={{ borderLeftColor: "var(--mos-amber)" }}
          data-testid="blocked-item-apex-slip"
        >
          <div className="flex items-start gap-4">
            <span
              className="mt-0.5 h-7 w-7 flex-none inline-flex items-center justify-center rounded-full"
              style={{ background: "var(--mos-amber-bg)" }}
            >
              <AlertTriangle size={14} style={{ color: "var(--mos-amber)" }} />
            </span>
            <div className="flex-1">
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="text-[16px] font-semibold text-slate-900">
                  {crisis.supplier} slip detected
                </span>
                <span
                  className="mos-mono text-[12px] px-2 py-0.5 rounded"
                  style={{
                    background: "var(--mos-amber-bg)",
                    color: "var(--mos-amber)",
                  }}
                >
                  {crisis.po}
                </span>
              </div>
              <div className="mt-2 text-[14px] text-slate-700 leading-relaxed">
                Revised promise date{" "}
                <span className="mos-mono text-slate-900">
                  {crisis.revisedDate}
                </span>{" "}
                (originally{" "}
                <span className="mos-mono text-slate-900">
                  {crisis.originalDate}
                </span>
                ). Downstream:{" "}
                <span className="mos-mono text-slate-900">
                  {crisis.salesOrder.id}
                </span>{" "}
                to {crisis.salesOrder.customer} committed{" "}
                <span className="mos-mono text-slate-900">
                  {crisis.salesOrder.committedDelivery}
                </span>
                .
              </div>

              <div className="mt-4 flex items-center gap-6 text-[13px]">
                <div>
                  <span className="text-slate-500 mr-1.5">Order value</span>
                  <span className="mos-mono text-slate-900 font-medium">
                    {crisis.salesOrder.orderValue}
                  </span>
                </div>
                <div className="h-3 w-px bg-slate-200" />
                <div>
                  <span className="text-slate-500 mr-1.5">Dependency</span>
                  <span className="mos-mono text-slate-900 font-medium">
                    {crisis.workOrder.id}
                  </span>
                  <span className="text-slate-500">
                    {" "}
                    · {crisis.workOrder.site} · starts{" "}
                    <span className="mos-mono text-slate-900">
                      {crisis.workOrder.scheduledStart}
                    </span>
                  </span>
                </div>
                <div className="h-3 w-px bg-slate-200" />
                <div>
                  <span className="text-slate-500 mr-1.5">Penalty clause</span>
                  <span className="mos-mono text-slate-900 font-medium">
                    {crisis.salesOrder.penaltyClause}
                  </span>
                  <span className="text-slate-500">
                    {" "}
                    · {crisis.salesOrder.penaltyTerms}
                  </span>
                </div>
              </div>
            </div>
            <ArrowRight
              size={18}
              className="text-slate-300 group-hover:text-slate-500 mt-1 transition-colors"
            />
          </div>
        </button>
      </section>

      {/* WHAT NEEDS A DECISION */}
      <section data-testid="section-needs-decision">
        <SectionLabel tone="slate">What needs a decision today</SectionLabel>
        <div className="mos-surface rounded-md p-6">
          <div className="flex items-start gap-4">
            <span
              className="mt-0.5 h-7 w-7 flex-none inline-flex items-center justify-center rounded-full border"
              style={{ borderColor: "var(--mos-border-strong)" }}
            >
              <HelpCircle size={14} className="text-slate-600" />
            </span>
            <div className="flex-1">
              <div className="text-[15px] font-semibold text-slate-900">
                Freight expedite vs customer notification
              </div>
              <p className="mt-2 text-[14px] text-slate-700 leading-relaxed max-w-3xl">
                Two ranked options are ready. Approve{" "}
                <span className="text-slate-900 font-medium">
                  {rec.title.toLowerCase()}
                </span>{" "}
                for{" "}
                <span className="mos-mono text-slate-900">
                  {rec.metrics[0].value}
                </span>
                , or accept the alternate slip with{" "}
                <span className="mos-mono text-slate-900">
                  {alt.metrics[2].value}
                </span>{" "}
                penalty exposure under clause{" "}
                <span className="mos-mono">
                  {crisis.salesOrder.penaltyClause}
                </span>
                .
              </p>
              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => navigate("/reasoning")}
                  className="mos-btn mos-btn-primary"
                  data-testid="btn-view-reasoning"
                >
                  View reasoning
                  <ArrowRight size={15} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PORTFOLIO ROLLUP · one summary line per feature (SP-06, F1/F2/F3/F4/F5 UI-03) */}
      <section data-testid="section-portfolio-rollup" className="no-print">
        <SectionLabel tone="slate">Portfolio rollup</SectionLabel>
        <div className="mos-surface rounded-md divide-y" style={{ borderColor: "var(--mos-border)" }}>
          <RollupRow
            testId="rollup-risk"
            label="Commitment risk"
            summary={
              <>
                <span className="mos-mono text-slate-900">{radar.summary.atRisk}</span> at-risk of{" "}
                <span className="mos-mono text-slate-900">{radar.summary.total}</span> · exposure{" "}
                <span className="mos-mono text-slate-900">{radar.summary.exposureTotal}</span> ·{" "}
                {radar.summary.netChange}
              </>
            }
            onClick={() => navigate("/risk")}
          />
          <RollupRow
            testId="rollup-qmsr"
            label="QMSR readiness"
            summary={
              <>
                <span className="mos-mono text-slate-900">{qmsr.summary.critical}</span> Critical ·{" "}
                <span className="mos-mono text-slate-900">{qmsr.summary.high}</span> High ·{" "}
                <span className="mos-mono text-slate-900">{qmsr.summary.medium}</span> Medium · {qmsr.summary.netChange}
              </>
            }
            onClick={() => navigate("/quality/crosswalk")}
          />
          <RollupRow
            testId="rollup-suppliers"
            label="Supplier file"
            summary={
              <>
                <span className="mos-mono text-slate-900">{suppliers.summary.lapsed}</span> lapsed ·{" "}
                <span className="mos-mono text-slate-900">{suppliers.summary.lapsingIn30 + suppliers.summary.lapsingIn90}</span>{" "}
                lapsing-in-90 · {suppliers.summary.netChange}
              </>
            }
            onClick={() => navigate("/suppliers")}
          />
          <RollupRow
            testId="rollup-assets"
            label="Asset reallocation memo"
            summary={
              <>
                Impact <span className="mos-mono text-slate-900">{assets.totalImpact}</span> ·{" "}
                {assets.moveKits.length} kits to move · {assets.recoverSets.length} set to recover · {assets.deferPurchases.length} capital deferral
              </>
            }
            onClick={() => navigate("/assets")}
          />
          <RollupRow
            testId="rollup-trace"
            label="Genealogy · last trace"
            summary={
              <>
                Seed <span className="mos-mono text-slate-900">{trace.seedNode.label}</span> ·{" "}
                {trace.scope.salesOrdersAffected} sales orders touched ·{" "}
                {trace.breaks.length > 0 ? (
                  <span style={{ color: "var(--mos-amber)" }}>
                    {trace.breaks.length} traceability break declared
                  </span>
                ) : (
                  "complete"
                )}
              </>
            }
            onClick={() => navigate("/genealogy")}
          />
        </div>
      </section>

      {/* Footer */}
      <footer
        className="pt-8 border-t text-[12px] text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-1"
        style={{ borderColor: "var(--mos-border)" }}
        data-testid="briefing-footer"
      >
        <span>
          Generated{" "}
          <span className="mos-mono text-slate-700">{briefing.generatedAt}</span>
        </span>
        <span className="text-slate-300">·</span>
        <span>Sources: {briefing.sources.join(" · ")}</span>
        <span className="text-slate-300">·</span>
        <span>Full trace available.</span>
      </footer>

      {/* Print-only signed-artifact footer */}
      <div className="print-only" style={{ marginTop: "24pt" }}>
        <div
          className="pt-4 border-t text-[9pt] text-slate-500 flex items-center justify-between"
          style={{ borderColor: "#0F172A" }}
        >
          <span>
            Sent to j.okafor@meridian.example · Archived to audit trail on generation.
          </span>
          <span className="mos-mono">rt-2026-05-23-0658</span>
        </div>
      </div>

      {/* Send approval gate */}
      <ApprovalModal
        open={sendOpen}
        onCancel={() => setSendOpen(false)}
        onApprove={onSendApproved}
        title="Send briefing"
        rationale="This action emails today's briefing to the addressee and archives a signed PDF copy to the audit trail. It does not modify any operational record."
        approver="J. Okafor"
        action="Send May 23 briefing to J. Okafor + archive PDF"
        systemsAffected={["Mailbox · outbound", "Audit archive"]}
        auditNote="Send event and archive reference will both write to the audit log."
        confirmLabel="Send and archive"
      />
    </div>
  );
}

function RollupRow({ label, summary, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors group"
      style={{ borderColor: "var(--mos-border)" }}
      data-testid={testId}
    >
      <span
        className="text-[11.5px] uppercase tracking-wider text-slate-500 font-semibold"
        style={{ minWidth: "170px" }}
      >
        {label}
      </span>
      <span className="flex-1 text-[13.5px] text-slate-700">{summary}</span>
      <ChevronRight
        size={16}
        className="text-slate-300 group-hover:text-slate-500 transition-colors flex-none"
      />
    </button>
  );
}
